import {
  Autocomplete,
  autocompleteClasses,
  Box,
  CircularProgress,
  darken,
  InputAdornment,
  styled,
  TextField,
} from "@mui/material";
import tagsService from "api/tagsService";
import CloseIcon from "assets/icons/close.svg?react";
import classNames from "classnames";
import Chip from "components/Chip";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import Spinner from "components/Spinner";

const InputWithChips: FC<Props> = ({
  onChooseTag = () => {},
  label,
  labelPosition = "top",
  onChange,
  defaultValue,
  value,
  disabledTags = false,
  onBlur,
  onFocus,
  onRightClick,
  onRightText,
  hideTags = false,
  focus = false,
  onAccept,
  variant = "filled",
  elements,
  chips,
  onChipDelete,
  checkCanAdd,
  forwardRef,
  limitTags,
  onLimitedTagsClick,
  disableBorder = false,
  rightPadding = 0,

  open,
  handleOpen,
  handleClose,
  isOptionEqualToValue,
  getOptionLabel,
  getOptionAdditional,
  getOptionIcon,
  renderOption,
  options = [],
  loading,
  groupBy,
  handleOnChange,
  getOptionDisabled,
}) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const textRef = useRef(null);
  const [isFocused] = useState(false);
  const [text, setText] = useState(defaultValue ?? "");

  const link = window.location.pathname;

  const isOutcoming = link.search("outcoming") !== -1;
  const isPisma = link.search("pisma") !== -1;

  const tagS = (type: string) => {
    if (isOutcoming) {
      return 7;
    }

    if (isPisma) {
      return 8;
    }

    switch (type) {
      case "cases":
        return 1;

      case "actions":
        return 0;

      case "clients":
        return 3;

      case "letters":
        return 4;

      case "packages":
        return 6;
      default:
        return -1;
    }
  };

  const currentWorkspaceId = getWorkspaceId();
  tagsService.useGetTags(
    disabledTags,
    tagS(link.split("/")[1]),
    undefined,
    currentWorkspaceId ?? "",
  );

  const [showTags, setShowTags] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const v = e.target.value;

    setText(v);
    onChange(v);
  };

  useEffect(() => {
    const formatedText = value ?? text;

    const isHashtag = formatedText.search("#") !== -1;

    if (isHashtag && !showTags && !hideTags) {
      setShowTags(true);
      return;
    }

    if (!isHashtag && showTags) {
      setShowTags(false);
      return;
    }
  }, [value, text]);

  const handleClickButton = (e: any) => {
    if ((e.key === "Enter" || e.code === "Space") && isFocused) {
      if (onAccept) {
        onAccept?.();
        return;
      }
      const formatedText = value ?? text;
      if (formatedText.search("#") === -1) return;
      const tag = formatedText.split("#")[1].replaceAll("_", " ");

      setShowTags(false);

      onChooseTag({
        Id: "-1",
        Name: tag,
        Type: 2,
        Flags: 0,
        Global: false,
      });

      setText(formatedText.split("#")[0]);
      onChange(formatedText.split("#")[0]);
    }
  };

  const handleChoose = () => {
    //@ts-ignore
    ref?.current?.focus();
  };

  useEffect(() => {
    if (!ref?.current || !focus) return;
    handleChoose();
  }, [ref, ref.current, focus]);

  useEffect(() => {
    document.addEventListener("keypress", handleClickButton);

    return () => document.removeEventListener("keypress", handleClickButton);
  }, [value, text, isFocused]);

  const getWidth = (fontSize, value) => {
    const div = document.createElement("div");
    div.innerText = value;
    div.style.fontSize = fontSize;
    div.style.width = "auto";
    div.style.display = "inline-block";
    div.style.visibility = "hidden";
    div.style.position = "fixed";
    div.style.overflow = "auto";
    document.body.append(div);
    const width = div.clientWidth;
    div.remove();
    return width;
  };

  useEffect(() => {
    if (textRef.current) {
      let width = getWidth(12, text);
      if (width < 80) {
        width = 80;
      }

      if (width > 800) {
        width = 800;
      }

      (textRef.current as HTMLInputElement).style.setProperty(
        "min-width",
        `${width}px`,
        "important",
      );
    }
  }, [text]); // Update width when text changes

  const getEndAndorment = () => {
    if (elements) {
      return (
        <div
          style={{
            position: "absolute",
            right: "4px",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          {elements}
        </div>
      );
    }

    if (onRightClick) {
      return (
        <div
          className={`${styles.clk} ${styles.button}`}
          onClick={onRightClick}
        >
          {onRightText}
        </div>
      );
    }

    if (value.length) {
      return (
        <CloseIcon
          className={classNames("cursor-pointer", styles.closeIcon)}
          onClick={() => {
            setText("");
            onChange("");
          }}
        />
      );

      return <></>;
    }
  };

  const getInputProps = () => {
    return (
      <InputAdornment position="end">
        {getEndAndorment()}

        {loading ? <CircularProgress color="inherit" size={20} /> : null}
      </InputAdornment>
    );
  };

  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  const toggleGroup = (groupName) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.has(groupName) ? next.delete(groupName) : next.add(groupName);
      return next;
    });
  };

  const GroupHeader = styled("div")(({ theme }) => ({
    position: "sticky",
    top: "-8px",
    padding: "4px 8px",
    color: "#1c1b1f",
    fontWeight: "400",
    backgroundColor: "#e5e1e6 ",
    ...theme.applyStyles("dark", {
      backgroundColor: darken(theme.palette.primary.main, 0.8),
    }),
    borderBottom: "1px solid #e5e0ec",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginBottom: "4px",
  }));

  const GroupItems = styled("ul")({
    padding: 0,
    marginBottom: "4px",
  });

  useEffect(() => {
    if (value.length) {
      const groups = options.map((x) => groupBy(x));

      if (groups.length && groups[0].startsWith("Lista adre")) {
        if (!collapsedGroups.has(groups[0])) {
          setCollapsedGroups((prev) => {
            const next = new Set(prev);
            next.add(groups[0]);

            return next;
          });
        }
      } else {
        setCollapsedGroups(new Set());
      }
    } else {
      setCollapsedGroups(new Set());
    }
  }, [value]);

  return (
    <Autocomplete
      // limitTags={1}
      // getLimitTagsText={(n) => (
      //   <div className={styles.limitTags}>
      //     <GroupIcon></GroupIcon> {"+ "}
      //     {n}
      //   </div>
      // )}
      onChange={(_event, newOptions, reason) => {
        if (handleOnChange) {
          handleOnChange(newOptions);
        }
        console.log(reason);

        checkCanAdd();
      }}
      clearIcon={false}
      freeSolo
      ref={forwardRef}
      inputValue={value}
      multiple
      sx={{
        "& .MuiInputBase-root": {
          display: "flex",
          alignItems: "center",
          paddingRight: `${rightPadding}px !important`,
        },
        "& MuiInputBase-root-MuiFilledInput-root:after)": {
          borderColor: "black",
        },
        "& .MuiAutocomplete-input": {
          minWidth: "80px !important",
        },
        ".MuiAutocomplete-listbox": {
          padding: 0,
        },
        ".MuiAutocomplete-option": {
          padding: "0 !important",
          margin: 0,
        },
      }}
      value={chips.map((chip) => chip.label)}
      renderTags={() => (
        <>
          {chips
            .slice(0, limitTags ? 1 : Number.MAX_VALUE)
            .map((option, index) => {
              if (!limitTags || chips.length < 2) {
                return (
                  <div className={styles.chipWrapper}>
                    <Chip
                      key={index}
                      onDelete={() => onChipDelete?.(option.id)}
                      onClick={() => onLimitedTagsClick?.()}
                      element={
                        <>
                          {option.label} {option.isBusy && <Spinner></Spinner>}
                        </>
                      }
                      error={!option.isValid}
                      icon={option.Icon}
                    />
                  </div>
                );
              } else {
                return (
                  <div
                    className={styles.chipWrapper}
                    onClick={() => onLimitedTagsClick?.()}
                  >
                    <Chip
                      key={index}
                      element={
                        <>
                          {`${option.label} +${chips.length - 1} `}
                          <FontAwesomeIcon icon={faCaretDown} />
                        </>
                      }
                    />
                  </div>
                );
              }
            })}

          {/* {!limitTags && chips.length > 1 && (
            <div
              className={styles.limitTags}
              onClick={() => setLimitTags(true)}
            >
              Zwiń
            </div>
          )} */}
        </>
      )}
      renderInput={(params) => {
        params.InputProps.endAdornment = getInputProps();
        params.InputProps.startAdornment = (
          <>
            {labelPosition === "start" && (
              <div className={styles.startAdornment}>{label}</div>
            )}
            {params.InputProps.startAdornment}
          </>
        );

        return (
          <TextField
            autoFocus={focus}
            onChange={(e) => {
              handleChange(e);
            }}
            onFocus={() => {
              onFocus?.();
            }}
            onBlur={() => {
              onBlur?.();
            }}
            label={labelPosition === "top" ? label : null}
            {...params}
            variant={variant}
            size="small"
            InputProps={{
              ...params.InputProps,
              disableUnderline: disableBorder,
              inputRef: textRef,
            }}
          />
        );
      }}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabel}
      options={options}
      loading={loading}
      loadingText={t("common.loading")}
      filterOptions={(x) => x}
      renderOption={(props, option, state, ownerState) => {
        const { key, ...optionProps } = props;
        const cntnt = renderOption(option);
        return (
          <Box
            key={key}
            sx={{
              borderRadius: "8px",
              margin: "5px",
              [`&.${autocompleteClasses.option}`]: {
                padding: "8px",
              },
            }}
            component="li"
            {...optionProps}
          >
            <div className={styles.optionIcon}>
              {getOptionIcon && getOptionIcon(option)}
            </div>
            <div className={styles.optionRows}>
              <div className={styles.optionRow}>
                {!cntnt && ownerState.getOptionLabel(option)}
                {cntnt && cntnt}
              </div>
              {getOptionAdditional && getOptionAdditional(option) && (
                <div className={classNames(styles.optionRow, styles.secRow)}>
                  {getOptionAdditional(option)}
                </div>
              )}
            </div>
          </Box>
        );
      }}
      groupBy={groupBy}
      renderGroup={(params) => {
        const isCollapsed = collapsedGroups.has(params.group);
        return (
          <li key={params.key}>
            <div
              onClick={() => toggleGroup(params.group)}
              style={{ cursor: "pointer" }}
            >
              <GroupHeader className={styles.group}>
                <div className={styles.groupHeader}>
                  {params.group.indexOf("_ID")
                    ? params.group.split("_ID")[0]
                    : params.group}
                </div>
                <div className={styles.expanderWrapper}>
                  {" "}
                  <FontAwesomeIcon
                    className={classNames(styles.expander)}
                    icon={!isCollapsed ? faCaretDown : faCaretUp}
                  />
                </div>
              </GroupHeader>
            </div>
            {!isCollapsed && <GroupItems>{params.children}</GroupItems>}
          </li>
        );
      }}
      getOptionDisabled={(option) => getOptionDisabled?.(option) ?? false}
    />
  );
};

export default InputWithChips;
